import { useEffect, useMemo, useState } from 'react'
import { CircleAlert, CircleCheck, CircleX, Lock, Pencil, Plus } from 'lucide-react'
import DataTable from '../../components/DataTable'
import { InfoNote } from '../../components/Form'
import IconButton from '../../components/IconButton'
import { useRemembered } from '../../components/listMemory'
import PageHeader from '../../components/PageHeader'
import SegmentedControl from '../../components/SegmentedControl'
import { DeniedState, ErrorState } from '../../components/States'
import StatStrip from '../../components/StatStrip'
import StatusBadge from '../../components/StatusBadge'
import TabBar from '../../components/TabBar'
import useHashTab from '../../components/useHashTab'
import { serverDetail } from '../../lib/i18n'
import { InlineActions, InlineInput } from './SettingsKeysInline'
import { listSettingsKeys, saveReferenceEntry } from './settingsService'
import { common as C, settingsKeysStrings as S } from './strings'
import './settings.css'

/**
 * A key resolves to whichever value the system will actually use:
 * the database wins, the coded default is the fallback, and neither means the key is
 * unset. The three counters at the top of the legacy page count exactly these.
 */
/** The three key states, mapped onto the strip's tones. */
const TONES = { good: 'success', warn: 'warning', bad: 'danger' }

const STATES = [
  { id: 'db', tone: 'good', icon: CircleCheck, label: S.databaseValue },
  { id: 'default', tone: 'warn', icon: CircleAlert, label: S.defaultValue },
  { id: 'none', tone: 'bad', icon: CircleX, label: S.none },
]

const stateOf = (row) => {
  if (row.dbValue !== null) return 'db'
  if (row.defaultValue !== null) return 'default'
  return 'none'
}

/* The two views this page answers to in its URL hash — the same contract every other
   tabbed screen here uses, so `#reference` opens the reference tables rather than the
   page's first tab. See components/useHashTab.js. */
const VIEWS = ['keys', 'reference']

/* The reference lists, in the legacy page's order. Each is its own table: they no longer
   share a shape — authentication methods carry one name, templates are read-only here. */
const GROUPS = ['activities', 'templates', 'groupTypes', 'difficultyLevels', 'courseLevels', 'authMethods']

const refId = (row) => `${row.group}-${row.id}`

/** بيانات الإعدادات — Views/SettingsKeys/Index.cshtml. */
export default function SettingsKeysPage() {
  const [rows, setRows] = useState([])
  const [reference, setReference] = useState([])
  const [ffmpegExists, setFfmpegExists] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  /* Which of the three counters is narrowing the list — remembered, because reading a
     key's value means opening something else and coming back, and the filter that found
     it went with the page. */
  const [filter, setFilter] = useRemembered('settings-keys:filter', null)
  const [rememberedGroup, setGroup] = useRemembered('settings-keys:group', GROUPS[0])
  const group = GROUPS.includes(rememberedGroup) ? rememberedGroup : GROUPS[0]
  const [view, setView] = useHashTab('keys', VIEWS)

  /* EDITING HAPPENS IN THE ROW. One row of each table is open at a time; its draft lives
     here because a reference row's two names sit in two different cells. */
  const [refDraft, setRefDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  /* The row just saved, marked once — a confirmation that stays in the table rather than
     a dialog to dismiss after every edit. */
  const [justSaved, setJustSaved] = useState(null)

  useEffect(() => {
    listSettingsKeys()
      .then((response) => {
        setRows(response.rows)
        setReference(response.reference)
        setFfmpegExists(response.ffmpegExists)
        setLoading(false)
      })
      .catch((error) => {
        setLoadError(error)
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (!justSaved) return undefined
    const timer = setTimeout(() => setJustSaved(null), 1600)
    return () => clearTimeout(timer)
  }, [justSaved])

  /* The reference tables matter when something is MISSING, so the count that leads is
     the count of gaps — not the total, which is a constant of the codebase. */
  const missingReference = useMemo(
    () => reference.filter((row) => !row.existsInDb).length,
    [reference],
  )

  const groupRows = useMemo(() => reference.filter((row) => row.group === group), [reference, group])

  /* ---- a reference row's names ---- */

  const single = group === 'authMethods'

  const refCanSave = (row) => {
    if (refDraft?.id !== refId(row)) return false
    const filled = single
      ? refDraft.name.trim() !== ''
      : refDraft.arabicName.trim() !== '' && refDraft.englishName.trim() !== ''
    const changed = single
      ? refDraft.name !== row.name
      : refDraft.arabicName !== row.arabicName || refDraft.englishName !== row.englishName
    /* A missing row is worth saving as soon as it is named; an existing one only once its
       names change. */
    return filled && (changed || !row.existsInDb)
  }

  const saveNames = async (row) => {
    if (!refCanSave(row) || saving) return
    const names = single
      ? { name: refDraft.name }
      : { arabicName: refDraft.arabicName, englishName: refDraft.englishName }
    setSaving(true)
    const outcome = await saveReferenceEntry(row, names)
    setSaving(false)
    if (!outcome.ok) {
      setRefDraft((draft) => draft && { ...draft, error: serverDetail(outcome.message) || C.saveFailed })
      return
    }
    setReference((current) =>
      current.map((item) => (refId(item) === refId(row) ? { ...item, ...names, existsInDb: true } : item)),
    )
    setRefDraft(null)
    setJustSaved(refId(row))
  }

  const setRefName = (field) => (value) =>
    setRefDraft((draft) => draft && { ...draft, [field]: value, error: '' })

  /* Leaving a table leaves its open row. */
  const closeDrafts = () => {
    setRefDraft(null)
  }

  const referenceColumns = useMemo(() => {
    const editable = group !== 'templates'
    const fields = single ? ['name'] : ['arabicName', 'englishName']
    const name = (key, dir) => ({
      key,
      header: S.reference[key],
      render: (row) =>
        refDraft?.id === refId(row) ? (
          <InlineInput
            value={refDraft[key]}
            onChange={setRefName(key)}
            onSave={() => saveNames(row)}
            onCancel={() => setRefDraft(null)}
            label={S.reference[key]}
            dir={dir}
            autoFocus={key === fields[0]}
            /* One refusal for the row, said once, under its first field. */
            error={key === fields[0] ? refDraft.error : ''}
            busy={saving}
          />
        ) : (
          <bdi
            dir={dir}
            className={[!row[key] && 'sk-empty', justSaved === refId(row) && 'sk-saved'].filter(Boolean).join(' ') || undefined}
          >
            {row[key] || '—'}
          </bdi>
        ),
    })

    return [
      {
        key: 'existsInDb',
        header: S.reference.status,
        width: '110px',
        searchable: false,
        value: (row) => (row.existsInDb ? 1 : 0),
        render: (row) => (
          <StatusBadge tone={row.existsInDb ? 'success' : 'danger'}>
            {row.existsInDb ? S.reference.present : S.reference.missing}
          </StatusBadge>
        ),
      },
      { key: 'id', header: S.reference.id, width: '72px' },
      {
        key: 'enumName',
        header: S.reference.enumName,
        render: (row) => (
          <span className="set-mono" dir="ltr">
            {row.enumName}
          </span>
        ),
      },
      ...(single ? [name('name', 'ltr')] : [name('arabicName', 'rtl'), name('englishName', 'ltr')]),
      ...(editable
        ? [
            {
              key: 'actions',
              header: <Pencil size={15} strokeWidth={1.75} />,
              width: '112px',
              align: 'center',
              sortable: false,
              searchable: false,
              /* A missing row is ADDED from here — the gap this tab reports is closed on
                 the tab that reports it. */
              render: (row) =>
                refDraft?.id === refId(row) ? (
                  <InlineActions
                    canSave={refCanSave(row)}
                    busy={saving}
                    onSave={() => saveNames(row)}
                    onCancel={() => setRefDraft(null)}
                  />
                ) : (
                  <IconButton
                    icon={row.existsInDb ? Pencil : Plus}
                    label={row.existsInDb ? S.reference.editTitle : S.reference.add}
                    onClick={() =>
                      setRefDraft({
                        id: refId(row),
                        arabicName: row.arabicName,
                        englishName: row.englishName,
                        name: row.name,
                        error: '',
                      })
                    }
                  />
                ),
            },
          ]
        : []),
    ]
    // The render functions read the draft, so the columns are rebuilt with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, refDraft, saving, justSaved])

  const counts = useMemo(
    () =>
      rows.reduce(
        (totals, row) => ({ ...totals, [stateOf(row)]: (totals[stateOf(row)] ?? 0) + 1 }),
        {},
      ),
    [rows],
  )

  const visible = useMemo(
    () => (filter ? rows.filter((row) => stateOf(row) === filter) : rows),
    [rows, filter],
  )

  const columns = useMemo(
    () => [
      {
        key: 'state',
        header: '',
        width: '56px',
        align: 'center',
        searchable: false,
        /* The legacy sorts this column on the same 0/1/2 order the icon encodes. */
        value: (row) => STATES.findIndex((state) => state.id === stateOf(row)),
        render: (row) => {
          const state = STATES.find((item) => item.id === stateOf(row))
          const Icon = state.icon
          return (
            <span className={`sk-mark sk-mark--${state.tone}`} title={state.label}>
              <Icon size={18} strokeWidth={2} />
            </span>
          )
        },
      },
      {
        key: 'key',
        header: S.key,
        width: '340px',
        render: (row) => (
          <span className="set-mono" dir="ltr">
            {row.key}
          </span>
        ),
      },
      {
        key: 'defaultValue',
        header: S.defaultValue,
        render: (row) => (
          <span className="set-mono set-mono--muted" dir="ltr">
            {row.defaultValue}
          </span>
        ),
      },
      {
        key: 'dbValue',
        header: S.databaseValue,
        render: (row) => (
          <span className="set-mono" dir="ltr">
            {row.sensitive && <Lock size={14} strokeWidth={2} aria-label={S.locked} />}
            {row.dbValue}
          </span>
        ),
      },
    ],
    // The render functions read the draft, so the columns are rebuilt with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  /* A REFUSED LOAD IS NOT A SLOW ONE — see SettingsPage. Without the catch a 403 left this
     screen with three counters of 0 over skeleton rows that never resolved, which read as
     "this system has no settings". */
  if (loadError) {
    return (
      <>
        <PageHeader title={S.title} />
        {loadError.denied ? (
          <DeniedState description={loadError.message} />
        ) : (
          <ErrorState title={C.loadFailed} description={loadError.message} />
        )}
      </>
    )
  }

  return (
    <>
      {/* The title was printed three times: header, header subtitle, table card. */}
      {/* The count of the list being SHOWN — it kept the settings total (155) over the
          reference tab, whose table holds a different number of rows. */}
      <PageHeader
        title={S.title}
        count={loading ? undefined : view === 'reference' ? groupRows.length : rows.length}
      />

      {/* Two things share this screen: what the installation is SET to, and whether the
          database still has the reference rows the code expects. Tabs rather than one
          long page, because they are read for different reasons. */}
      <TabBar
        items={[
          { id: 'keys', label: S.title },
          {
            id: 'reference',
            label: missingReference
              ? `${S.reference.title} (${missingReference})`
              : S.reference.title,
          },
        ]}
        value={view}
        onChange={(id) => {
          closeDrafts()
          setView(id)
        }}
        ariaLabel={S.title}
      />

      {/* The three counters become the filter. They were already computed and
          already sitting above the table; clicking one is the obvious thing to
          try, and until now it did nothing. */}
      {view === 'keys' && (
        <>
      <StatStrip
        items={STATES.map((state) => ({
          id: state.id,
          icon: state.icon,
          label: state.label,
          value: counts[state.id] ?? 0,
          tone: TONES[state.tone],
        }))}
        label={S.title}
        activeId={filter}
        onSelect={(id) => setFilter((current) => (current === id ? null : id))}
      />

      {/*
       * Paged.
       *
       * The legacy turns paging off and prints all six hundred keys, which is a
       * nine-thousand-pixel page you can only navigate with the scrollbar — by
       * a distance the least readable screen in the panel. Nothing is hidden:
       * the search box spans every key and the three counters above narrow by
       * state, which is how anyone actually looks for one.
       */}
      <DataTable
        columns={columns}
        rows={visible}
        loading={loading}
        /* The counters above are a filter the table cannot see: page 6 of six hundred
           keys stayed page 6 after narrowing to the eleven that are unset. */
        resetKey={filter ?? ''}
        getRowId={(row) => row.key}
      />
        </>
      )}

      {view === 'reference' && (
        <>
          {/* One line, but it explains a whole class of "the video never processed"
              reports, so it leads rather than hiding in a table. */}
          <p className="set-ffmpeg">
            <StatusBadge tone={ffmpegExists ? 'success' : 'danger'}>
              {ffmpegExists ? S.reference.present : S.reference.missing}
            </StatusBadge>
            <bdi>{S.reference.ffmpeg}</bdi>
          </p>

          <SegmentedControl
            variant="underline"
            items={GROUPS.map((id) => ({
              id,
              label: S.reference.groups[id],
              count: loading ? undefined : reference.filter((row) => row.group === id).length,
            }))}
            value={group}
            onChange={(id) => {
              setRefDraft(null)
              setGroup(id)
            }}
            ariaLabel={S.reference.title}
          />

          {group === 'templates' && <InfoNote>{S.reference.readOnly}</InfoNote>}

          <DataTable
            /* A second list on the same screen: the address already belongs to the first. */
            pageParam={null}
            columns={referenceColumns}
            rows={groupRows}
            loading={loading}
            resetKey={group}
            initialSort={{ key: 'existsInDb', dir: 'asc' }}
            getRowId={refId}
          />
        </>
      )}
    </>
  )
}
